import { QueryClient } from "@tanstack/react-query";

/**
 * Build a fresh {@link QueryClient}. The server MUST use one per request — a
 * shared singleton would leak one visitor's cache into another's response; the
 * browser reuses a single client ({@link getQueryClient}).
 *
 * A non-zero `staleTime` keeps SSR-seeded data fresh so it doesn't refetch on
 * mount, and focus/reconnect refetch is off — an unprompted refetch would also
 * swap draft content for published, since the SSR-seeded page queries aren't
 * draft-aware. (The lead reads are: they take `draft` and key on it, so their
 * refetches stay in whichever mode the page is in.)
 */
export function makeQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 60_000,
				refetchOnWindowFocus: false,
				refetchOnReconnect: false,
			},
		},
	});
}

let browserQueryClient: QueryClient | undefined;

/**
 * The browser's {@link QueryClient} singleton, reused for the page's lifetime so
 * the server-hydrated cache survives re-renders. It's fresh per document load
 * (this is an MPA). Server code MUST use {@link makeQueryClient} per request.
 */
export function getQueryClient(): QueryClient {
	return (browserQueryClient ??= makeQueryClient());
}
